export type AppErrorKind =
  | 'transport'
  | 'api'
  | 'auth'
  | 'player'
  | 'validation'
  | 'telegram_sdk'
  | 'timeout'
  | 'unknown'

export type AppErrorContext = Record<string, any>

/**
 * AppError: единый тип ошибки для мини-аппа.
 * Задача: стабильно классифицировать, не терять cause/stack, и облегчить группировку/дедуп.
 */
export class AppError extends Error {
  kind: AppErrorKind
  code: string
  status: number | null
  context: AppErrorContext
  cause: any

  constructor(opts: {
    kind: AppErrorKind
    code: string
    message: string
    status?: number | null
    context?: AppErrorContext
    cause?: any
  }) {
    super(opts.message)
    this.name = 'AppError'
    this.kind = opts.kind
    this.code = opts.code
    this.status = (opts.status === undefined) ? null : opts.status
    this.context = opts.context || {}
    this.cause = opts.cause
  }
}

function safeString(x: any, limit: number): string {
  try {
    const s = String(x ?? '')
    return s.length > limit ? s.slice(0, limit) : s
  } catch {
    return ''
  }
}

export function normalizeError(err: any, fallback?: { kind?: AppErrorKind; code?: string; context?: AppErrorContext }): AppError {
  if (err instanceof AppError) return err

  const kind = (fallback?.kind || 'unknown') as AppErrorKind
  const code = safeString(fallback?.code || 'unknown_error', 80) || 'unknown_error'
  const ctx = fallback?.context || {}

  // Native Error
  try {
    const msg = (err && typeof err === 'object' && 'message' in err) ? String((err as any).message || '') : ''
    const message = safeString(msg || safeString(err, 200) || 'Unknown error', 400)
    return new AppError({ kind, code, message, status: null, context: ctx, cause: err })
  } catch {
    return new AppError({ kind, code, message: 'Unknown error', status: null, context: ctx, cause: err })
  }
}

export function stackFromAny(err: any): string | null {
  try {
    if (err && typeof err === 'object' && (err as any).stack) return safeString((err as any).stack, 32000) || null
  } catch {}
  return null
}

